import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  UserIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  CommandLineIcon,
  GlobeAltIcon,
  ServerIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  SignalIcon,
  UsersIcon,
  BoltIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';

// Icon mapping for highlights
const ICON_MAP = {
  'trending-up': ArrowTrendingUpIcon,
  'trending-down': ArrowTrendingDownIcon,
  'shield-exclamation': ShieldExclamationIcon,
  'shield': ShieldCheckIcon,
  'exclamation-circle': ExclamationCircleIcon,
  'cpu-chip': CpuChipIcon,
  'users': UsersIcon,
  'server': ServerIcon,
  'globe': GlobeAltIcon,
  'signal': SignalIcon,
  'bolt': BoltIcon,
};

// Color classes
const COLOR_CLASSES = {
  blue: { bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  green: { bg: 'bg-green-500', light: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
  red: { bg: 'bg-red-500', light: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  yellow: { bg: 'bg-yellow-500', light: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-200' },
  purple: { bg: 'bg-purple-500', light: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  indigo: { bg: 'bg-indigo-500', light: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
  gray: { bg: 'bg-gray-500', light: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
  orange: { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
};

// Mini sparkline chart tooltip
function MiniTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="bg-gray-900 text-white px-2 py-1 rounded text-xs shadow-lg">
      <p>{new Date(data.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
      <p className="font-medium">{data.total} events</p>
    </div>
  );
}

// Stat box component
function StatBox({ label, value, sublabel, color = 'gray', trend, icon: Icon }) {
  const colors = COLOR_CLASSES[color] || COLOR_CLASSES.gray;

  return (
    <div className={`relative p-3 rounded-xl ${colors.light} border ${colors.border} overflow-hidden`}>
      {Icon && (
        <div className="absolute top-2 right-2 opacity-10">
          <Icon className={`w-8 h-8 ${colors.text}`} />
        </div>
      )}
      <p className={`text-2xl font-bold ${colors.text}`}>{value}</p>
      <p className="text-xs text-gray-600 font-medium">{label}</p>
      {sublabel && <p className="text-[10px] text-gray-400 mt-0.5">{sublabel}</p>}
      {trend !== undefined && trend !== 0 && (
        <div className={`flex items-center gap-0.5 mt-1 text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend > 0 ? <ArrowTrendingUpIcon className="w-3 h-3" /> : <ArrowTrendingDownIcon className="w-3 h-3" />}
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
  );
}

// Highlight card component - informational, not clickable
function HighlightCard({ highlight }) {
  const Icon = ICON_MAP[highlight.icon] || BoltIcon;
  const colors = COLOR_CLASSES[highlight.color] || COLOR_CLASSES.gray;

  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl ${colors.light} border ${colors.border}`}>
      <div className={`p-2 rounded-lg ${colors.bg} bg-opacity-20`}>
        <Icon className={`w-5 h-5 ${colors.text}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${colors.text}`}>{highlight.title}</p>
        {highlight.description && (
          <p className="text-xs text-gray-500 mt-0.5 truncate">{highlight.description}</p>
        )}
      </div>
    </div>
  );
}

// Top actor row
function TopActorRow({ label, value, sublabel, color = 'gray', onClick }) {
  const colors = COLOR_CLASSES[color] || COLOR_CLASSES.gray;

  return (
    <div
      onClick={onClick}
      className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className={`w-2 h-2 rounded-full ${colors.bg}`} />
        <span className="text-sm text-gray-700 truncate group-hover:text-blue-600">{label}</span>
      </div>
      <div className="text-right">
        <span className="text-sm font-semibold text-gray-900">{value}</span>
        {sublabel && <span className="text-xs text-gray-400 ml-1">{sublabel}</span>}
      </div>
    </div>
  );
}

// Convert time range string to hours
const TIME_RANGE_TO_HOURS = {
  '1h': 1,
  '6h': 6,
  '24h': 24,
  '7d': 168,
  '30d': 720,
};

export default function LogsCategoryBreakdown({ logs, stats, timeRange = '24h' }) {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Convert timeRange prop to hours
  const hoursBack = TIME_RANGE_TO_HOURS[timeRange] || 24;

  // Fetch dashboard analytics when timeRange changes
  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const hours = TIME_RANGE_TO_HOURS[timeRange] || 24;
      const response = await axiosInstance.get(`/logs/dashboard/analytics?hours=${hours}`);
      setAnalytics(response.data.data);
    } catch (err) {
      console.error('Error fetching dashboard analytics:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to activity profile
  const navigateToActivity = (type, id) => {
    if (id) {
      navigate(`/dashboard/logs/activity/${type}/${encodeURIComponent(id)}`);
    }
  };

  // Calculate health status color
  const healthColor = useMemo(() => {
    if (!analytics?.systemHealth) return 'green';
    const status = analytics.systemHealth.status;
    if (status === 'unhealthy') return 'red';
    if (status === 'degraded') return 'yellow';
    return 'green';
  }, [analytics]);

  // Security threat color
  const threatColor = useMemo(() => {
    if (!analytics?.securityInsights) return 'green';
    const level = analytics.securityInsights.threatLevel;
    if (level === 'high') return 'red';
    if (level === 'medium') return 'yellow';
    return 'green';
  }, [analytics]);

  if (loading && !analytics) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-5 gap-3 mb-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <ExclamationCircleIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>{error}</p>
          <button
            onClick={fetchAnalytics}
            className="mt-2 text-sm text-blue-600 hover:text-blue-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const { coreStats, activityTimeline, topActors, securityInsights, systemHealth, comparisons, highlights } = analytics || {};

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BoltIcon className="w-5 h-5 text-indigo-500" />
            <h3 className="text-sm font-bold text-gray-900">System Intelligence</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
              {timeRange}
            </span>
            <button
              onClick={fetchAnalytics}
              disabled={loading}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Core Stats Row */}
        <div className="grid grid-cols-5 gap-2">
          <StatBox
            label="Total Events"
            value={coreStats?.total?.toLocaleString() || '0'}
            color="indigo"
            icon={ServerIcon}
            trend={comparisons?.vsPrevious?.activityChange}
          />
          <StatBox
            label="Unique IPs"
            value={coreStats?.uniqueIPs?.toLocaleString() || '0'}
            color="blue"
            icon={GlobeAltIcon}
          />
          <StatBox
            label="Active Users"
            value={coreStats?.uniqueUsers?.toLocaleString() || '0'}
            color="purple"
            icon={UsersIcon}
          />
          <StatBox
            label="Devices"
            value={coreStats?.uniqueDevices?.toLocaleString() || '0'}
            color="green"
            icon={CpuChipIcon}
          />
          <StatBox
            label="Errors"
            value={coreStats?.byLevel?.error?.toLocaleString() || '0'}
            sublabel={coreStats?.errorRate > 0 ? `${coreStats.errorRate}% rate` : null}
            color={coreStats?.byLevel?.error > 10 ? 'red' : 'gray'}
            icon={ExclamationCircleIcon}
            trend={comparisons?.vsPrevious?.errorChange}
          />
        </div>

        {/* Activity Sparkline & Health */}
        <div className="grid grid-cols-3 gap-3">
          {/* Mini Timeline Chart */}
          <div className="col-span-2 bg-gray-50 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600">Activity Timeline</span>
              {activityTimeline?.summary && (
                <span className="text-xs text-gray-400">
                  Peak: {activityTimeline.summary.peakCount} at {
                    activityTimeline.summary.peakHour
                      ? new Date(activityTimeline.summary.peakHour).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : '-'
                  }
                </span>
              )}
            </div>
            <div className="h-16">
              {activityTimeline?.points?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={activityTimeline.points} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="activityGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="timestamp" hide />
                    <YAxis hide />
                    <Tooltip content={<MiniTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke="#6366F1"
                      strokeWidth={2}
                      fill="url(#activityGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-gray-400">
                  No timeline data
                </div>
              )}
            </div>
          </div>

          {/* Health & Security Quick View */}
          <div className="space-y-2">
            <div className={`p-2.5 rounded-xl ${COLOR_CLASSES[healthColor].light} border ${COLOR_CLASSES[healthColor].border}`}>
              <div className="flex items-center gap-2">
                {healthColor === 'green' ? (
                  <CheckCircleIcon className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircleIcon className={`w-4 h-4 ${COLOR_CLASSES[healthColor].text}`} />
                )}
                <span className={`text-xs font-semibold ${COLOR_CLASSES[healthColor].text}`}>
                  System {systemHealth?.status || 'healthy'}
                </span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {systemHealth?.systemErrors || 0} errors
              </p>
            </div>
            <div className={`p-2.5 rounded-xl ${COLOR_CLASSES[threatColor].light} border ${COLOR_CLASSES[threatColor].border}`}>
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className={`w-4 h-4 ${COLOR_CLASSES[threatColor].text}`} />
                <span className={`text-xs font-semibold ${COLOR_CLASSES[threatColor].text}`}>
                  {securityInsights?.threatLevel === 'low' ? 'Secure' : `${securityInsights?.threatLevel} risk`}
                </span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {securityInsights?.sshFailures || 0} SSH failures
              </p>
            </div>
          </div>
        </div>

        {/* Smart Highlights */}
        {highlights && highlights.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Insights</p>
            <div className="grid grid-cols-2 gap-2">
              {highlights.slice(0, 4).map((highlight, idx) => (
                <HighlightCard key={idx} highlight={highlight} />
              ))}
            </div>
          </div>
        )}

        {/* Top Actors */}
        <div className="grid grid-cols-3 gap-3">
          {/* Top IPs */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Top IPs</p>
            <div className="space-y-0.5">
              {topActors?.ips?.slice(0, 3).map((ip, idx) => (
                <TopActorRow
                  key={idx}
                  label={ip.ip}
                  value={ip.count.toLocaleString()}
                  color={ip.hasErrors ? 'red' : 'blue'}
                  onClick={() => navigateToActivity('ip', ip.ip)}
                />
              ))}
              {(!topActors?.ips || topActors.ips.length === 0) && (
                <p className="text-xs text-gray-400 py-2">No IP data</p>
              )}
            </div>
          </div>

          {/* Top Users */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Top Users</p>
            <div className="space-y-0.5">
              {topActors?.users?.slice(0, 3).map((user, idx) => (
                <TopActorRow
                  key={idx}
                  label={user.email.split('@')[0]}
                  value={user.count.toLocaleString()}
                  sublabel={user.role}
                  color="purple"
                  onClick={() => navigateToActivity('email', user.email)}
                />
              ))}
              {(!topActors?.users || topActors.users.length === 0) && (
                <p className="text-xs text-gray-400 py-2">No user data</p>
              )}
            </div>
          </div>

          {/* Top Actions */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Top Actions</p>
            <div className="space-y-0.5">
              {topActors?.actions?.slice(0, 3).map((action, idx) => (
                <TopActorRow
                  key={idx}
                  label={action.action}
                  value={action.count.toLocaleString()}
                  color="gray"
                />
              ))}
              {(!topActors?.actions || topActors.actions.length === 0) && (
                <p className="text-xs text-gray-400 py-2">No action data</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
